import React, { useEffect, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Paper,
  Divider,
  alpha,
} from "@mui/material";
import axios from "axios";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import {
  ASSETICONBASEURL,
  showToast,
  WEBSERVICE,
} from "../../../CoreDataService";
import { useSelector } from "react-redux";
import { ContainedButton, OutlinedButton } from "../../../Common/Buttons";

const LoanHistory = () => {
  const { btncolor } = useSelector((state) => state);
  const [tabValue, setTabValue] = useState(0);
  const [flexibleHistory, setFlexibleHistory] = useState([]);
  const [stableHistory, setStableHistory] = useState([]);
  const [autoInvestModalOpen, setAutoInvestModalOpen] = useState(false);

  useEffect(() => {
    fetchLoanHistory();
  }, []);

  const fetchLoanHistory = async () => {
    const payload = { uuid: localStorage.getItem("uuid") };
    try {
      const response = await axios.post(
        `${WEBSERVICE}crypto/banking/loan/getLoanHistory`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.data.error?.error_data !== 0) {
        showToast("error", response.data.error.error_msg);
        return;
      }

      const history = response.data.historyDetails || [];
      const flex = [];
      const stable = [];

      history.forEach((item) => {
        if (item.loanType.toLowerCase() === "flexible") {
          flex.push(item);
        } else {
          stable.push(item);
        }
      });

      setFlexibleHistory(flex);
      setStableHistory(stable);
    } catch (err) {}
  };

  const getStatusColor = (status) => {
    if (!status) return "var(--text-color)";
    const s = status.toLowerCase();
    if (s.includes("active") || s.includes("approved")) return "#16a34a";
    if (s.includes("liquidat") || s.includes("reject")) return "#ef4444";
    if (s.includes("repaid") || s.includes("closed")) return "#6366f1";
    return "var(--text-color)";
  };

  const getStatusBg = (status) => {
    if (!status) return "transparent";
    const s = status.toLowerCase();
    if (s.includes("active") || s.includes("approved"))
      return "rgba(22,163,74,0.1)";
    if (s.includes("liquidat") || s.includes("reject"))
      return "rgba(239,68,68,0.1)";
    if (s.includes("repaid") || s.includes("closed"))
      return "rgba(99,102,241,0.1)";
    return "rgba(125,125,125,0.1)";
  };

  const flexColumns = [
    "Loan ID",
    "Loan Date",
    "Borrowed Asset",
    "Borrowed Amount",
    "Collateral Asset",
    "Collateral Amount",
    "Interest Rate / Min",
    "Initial LTV",
    "Liquidation LTV",
    "Repayment Amount",
    "Remaining Amount",
    "Status",
  ];

  const stableColumns = [
    "Loan ID",
    "Loan Date",
    "Borrowed Asset",
    "Borrowed Amount",
    "Collateral Asset",
    "Collateral Amount",
    "Interest Rate / Min",
    "Initial LTV",
    "Liquidation LTV",
    "Loan Term",
    "Repayment Amount",
    "Remaining Amount",
    "Status",
  ];

  const renderAssetCell = (asset) => (
    <Tooltip title={asset}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <img
          src={`${ASSETICONBASEURL}${asset}.png`}
          height="20"
          width="20"
          style={{ borderRadius: "50%", flexShrink: 0 }}
          alt={asset}
        />
        <Typography sx={{ fontSize: "0.85rem", color: "var(--text-color)", whiteSpace: "nowrap" }}>
          {asset}
        </Typography>
      </Box>
    </Tooltip>
  );

  const renderTable = (rows, isStable = false) => {
    const columns = isStable ? stableColumns : flexColumns;

    return (
      <Box sx={resultsBlock}>
        <Box sx={resultsHeader}>
          <Box>
            <Typography sx={resultsTitle}>
              {isStable ? "Stable Rate" : "Flexible Rate"} Loan History
            </Typography>
            <Typography sx={resultsCaption}>
              {rows.length} record{rows.length !== 1 ? "s" : ""} found
            </Typography>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={modernTableContainer}>
          <Table sx={tableStyle}>
            <TableHead>
              <TableRow sx={modernTableHeadRow}>
                {columns.map((col, i) => (
                  <TableCell key={i} sx={modernTableHeadCell}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row, index) => (
                  <TableRow key={index} sx={modernTableBodyRow}>
                    <TableCell sx={modernTableBodyCell}>{row.loanId}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.createdOn}</TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {renderAssetCell(row.loanableAsset)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.borrowingAmount}</TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      {renderAssetCell(row.collateralAsset)}
                    </TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.collateralAmount}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.annualizedInterestRate}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.ltv}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.liquidationLTV}</TableCell>
                    {isStable && (
                      <TableCell sx={modernTableBodyCell}>{row.loanType}</TableCell>
                    )}
                    <TableCell sx={modernTableBodyCell}>{row.repaymentAmount}</TableCell>
                    <TableCell sx={modernTableBodyCell}>{row.remainingAmount}</TableCell>
                    <TableCell sx={modernTableBodyCell}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 1.25,
                          py: 0.4,
                          borderRadius: "20px",
                          backgroundColor: getStatusBg(row.description),
                          color: getStatusColor(row.description),
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.description}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={emptyCell}>
                    <InboxOutlinedIcon sx={emptyIcon} />
                    <Typography sx={emptyText}>No loan history found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={pageWrapper}>
      {/* Hero Banner */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <HistoryOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography sx={heroTitle}>Loan History</Typography>
            <Typography sx={heroSubtitle}>
              View your complete flexible and stable rate loan records
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Card */}
      <Box sx={mainCard}>
        {/* Tabs */}
        <Box sx={tabsWrap}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              ...tabsStyle,
              "& .MuiTabs-indicator": {
                backgroundColor: btncolor,
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .Mui-selected": { color: `${btncolor} !important` },
            }}
          >
            <Tab
              iconPosition="start"
              icon={<TrendingUpOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Flexible Rate"
              onClick={fetchLoanHistory}
              sx={tabItem}
            />
            <Tab
              iconPosition="start"
              icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Stable Rate"
              onClick={fetchLoanHistory}
              sx={tabItem}
            />
          </Tabs>
        </Box>

        <Box sx={tabPanel}>
          {tabValue === 0 && renderTable(flexibleHistory)}
          {tabValue === 1 && renderTable(stableHistory, true)}
        </Box>
      </Box>

      {/* Auto-Invest Confirmation Dialog */}
      <Dialog
        open={autoInvestModalOpen}
        onClose={() => setAutoInvestModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>Auto Invest</DialogTitle>
        <Divider />
        <DialogContent sx={dialogContentStyle}>
          <Typography sx={dialogText}>
            What happens when this auto-invest feature is turned off?
          </Typography>
          <Typography sx={{ ...dialogText, mt: 1.5 }}>
            (1) It will stop auto-investing for you.
          </Typography>
          <Typography sx={{ ...dialogText, mt: 1 }}>
            (2) Your already invested assets will remain in the Earn wallet and
            continue to earn the corresponding flexible product interest.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={dialogActionsStyle}>
          <ContainedButton
            onClick={() => {
              setAutoInvestModalOpen(false);
            }}
            variant="contained"
            sx={{ minWidth: 100 }}
          >
            Ok
          </ContainedButton>
          <OutlinedButton
            onClick={() => setAutoInvestModalOpen(false)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </OutlinedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Styles (mirrors Report.js / RecurringPayment.js conventions) ─────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const tabsWrap = {
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  px: { xs: 1, md: 2 },
  backgroundColor: "var(--body-color-less2)",
};

const tabsStyle = {
  minHeight: 56,
  "& .MuiTab-root": { minHeight: 56 },
};

const tabItem = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "var(--text-color)",
  opacity: 0.65,
  letterSpacing: "0.01em",
  px: 2,
  gap: 0.75,
  "&.Mui-selected": { color: "var(--text-color)", opacity: 1 },
};

const tabPanel = {
  p: { xs: 2, md: 3 },
};

const resultsBlock = {
  mt: 1,
};

const resultsHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1.5,
  mb: 2,
};

const resultsTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const resultsCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.65,
  mt: 0.25,
};

const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflowX: "auto",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 650,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "85%", sm: 440 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.15rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
};

const dialogContentStyle = {
  pt: 3,
  pb: 2,
  backgroundColor: "var(--popupbody-color)",
};

const dialogText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.85,
  lineHeight: 1.6,
};

const dialogActionsStyle = {
  justifyContent: "center",
  gap: 1,
  pb: 2.5,
  px: 2.5,
  backgroundColor: "var(--popupbody-color)",
};

export default LoanHistory;
